import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getBackendUrl } from '../../services/origin';
import { DEFAULT_BRANDING, PIN_STORE_PREFIX, DASHBOARD_TOKEN_KEY } from './constants';
import { VerifyScreen } from './VerifyScreen';
import { LoadingScreen, InvalidLink } from './Status';
import { Dashboard } from './Dashboard';
import CookieConsentBanner from '../../components/CookieConsentBanner';
import { loadTracking } from '../../services/tracking';

const API = getBackendUrl();

export default function CustomerDashboard() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const pinFromUrl = searchParams.get('pin') || '';
  const tabFromUrl = searchParams.get('tab') || '';
  const cachedPin = (typeof window !== 'undefined' && window.localStorage)
    ? (localStorage.getItem(PIN_STORE_PREFIX + token) || '')
    : '';
  const initialPin = pinFromUrl || cachedPin;
  const [verified, setVerified] = useState(false);
  const [pin, setPin] = useState(initialPin);
  const [verifying, setVerifying] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [brand, setBrand] = useState(DEFAULT_BRANDING);
  const [businessName, setBusinessName] = useState('');
  const [brandId, setBrandId] = useState('');
  const [brandLoading, setBrandLoading] = useState(true);

  // Fetch branding on mount (before PIN)
  useEffect(() => {
    axios.get(`${API}/api/my/${token}/branding`)
      .then(r => {
        setBrand({ ...DEFAULT_BRANDING, ...(r.data.branding || {}) });
        setBusinessName(r.data.business_name || '');
        if (r.data.brand_id) {
          setBrandId(r.data.brand_id);
          // Load tracking pixels for this brand. The customer dashboard
          // is post-registration so consent for marketing was already
          // captured, but we still respect Consent Mode v2 — events
          // fire only after the cookie banner choice.
          loadTracking(r.data.brand_id);
        }
      })
      .catch(() => {
        setError('Link non valido');
      })
      .finally(() => setBrandLoading(false));
  }, [token]);

  // Inject a per-page web manifest so when the customer installs the page
  // as a PWA (Android Chrome), the "start_url" points back to *this*
  // dashboard (with the token preserved), not the generic root manifest.
  // iOS Safari ignores manifest.start_url and uses the current URL, but
  // having the right name/icons here still improves the install title.
  useEffect(() => {
    const dynamicManifest = {
      name: businessName ? `${businessName} · La tua bacheca` : 'La tua bacheca',
      short_name: businessName || 'Bacheca',
      start_url: window.location.pathname + window.location.search,
      scope: '/',
      display: 'standalone',
      orientation: 'portrait',
      background_color: brand.background_color || '#111114',
      theme_color: brand.primary_color || '#D44E20',
      icons: [
        {
          src: brand.logo_url || `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 192 192'><rect width='192' height='192' rx='40' fill='${encodeURIComponent(brand.primary_color || '#D44E20')}'/><text x='96' y='130' font-size='100' text-anchor='middle' fill='white' font-family='sans-serif' font-weight='bold'>${(businessName || 'L').charAt(0).toUpperCase()}</text></svg>`,
          sizes: '192x192',
          type: brand.logo_url ? 'image/png' : 'image/svg+xml',
          purpose: 'any',
        },
      ],
      lang: 'it',
    };
    const blob = new Blob([JSON.stringify(dynamicManifest)], { type: 'application/manifest+json' });
    const url = URL.createObjectURL(blob);
    let link = document.querySelector('link[rel="manifest"]');
    const previous = link?.href;
    if (link) link.href = url;
    // Update theme-color too so iOS chrome matches the brand
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta && brand.primary_color) themeMeta.setAttribute('content', brand.primary_color);
    return () => {
      URL.revokeObjectURL(url);
      if (link && previous) link.href = previous;
    };
  }, [businessName, brand.primary_color, brand.background_color, brand.logo_url]);

  // Auto-verify if a PIN is available (URL or cached) — keeps the session
  // across refreshes so the dashboard feels like an installed app.
  useEffect(() => {
    if (initialPin && initialPin.length === 4 && !verified && !brandLoading) {
      handleVerifyWithPin(initialPin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandLoading]);

  // PWA install / standalone tracking — fires once per session per token.
  // We listen to `appinstalled` (Android Chrome) and detect standalone
  // mode (iOS / installed Android). The backend stamps `pwa_installed_at`
  // on first event so admin can see install conversion in Overview.
  useEffect(() => {
    if (!verified) return;
    const KEY = `lazo_pwa_signal_${token}`;
    const alreadySent = sessionStorage.getItem(KEY) === '1';
    const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator?.standalone === true;

    const send = (action) => {
      if (alreadySent) return;
      sessionStorage.setItem(KEY, '1');
      try { window.posthog?.capture?.('pwa_app_signal', { action, token }); } catch {}
      axios.post(`${API}/api/my/${token}/pwa-event`, { action }).catch(() => {});
    };

    // Detected standalone right now → stamp it
    if (isStandalone) send('standalone');

    // Fresh install event (Android Chrome)
    const onInstalled = () => {
      try { window.posthog?.capture?.('pwa_app_installed', { token }); } catch {}
      // Allow re-send of the install signal even if we already sent
      // 'standalone' once, since this is the more meaningful action.
      sessionStorage.removeItem(KEY);
      send('installed');
    };
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, [verified, token]);

  const handleVerifyWithPin = async (pinValue) => {
    const code = pinValue || pin;
    if (code.length !== 4) { toast.error('Inserisci il PIN a 4 cifre'); return; }
    setVerifying(true);
    try {
      await axios.post(`${API}/api/my/${token}/verify`, { pin: code });
      setVerified(true);
      // Persist on success only — invalid PINs must not be cached.
      try { localStorage.setItem(PIN_STORE_PREFIX + token, code); } catch {}
      // Persist the dashboard token (NOT the PIN) under a stable key
      // so cross-page surfaces (campaign landing reached via a printed
      // QR with no `?dt=` param) can auto-recognize the customer.
      try { localStorage.setItem(DASHBOARD_TOKEN_KEY, token); } catch {}
      // Strip `?pin=` from the URL so it isn't visible / shareable /
      // copied by accident. The PIN now lives in localStorage; future
      // visits to /my/<token> auto-resume from there. If localStorage
      // is wiped (incognito, manual clear), the verify screen kicks
      // back in and asks the customer to type the PIN again.
      if (typeof window !== 'undefined' && window.history?.replaceState) {
        try {
          const u = new URL(window.location.href);
          if (u.searchParams.has('pin')) {
            u.searchParams.delete('pin');
            window.history.replaceState({}, '', u.pathname + (u.searchParams.toString() ? '?' + u.searchParams.toString() : '') + u.hash);
          }
        } catch { /* ignore url manip errors */ }
      }
      setLoading(true);
      const res = await axios.get(`${API}/api/my/${token}/data?pin=${code}`);
      setData(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Errore di verifica';
      // Drop bad cached PIN so we don't loop on every refresh.
      try { localStorage.removeItem(PIN_STORE_PREFIX + token); } catch {}
      toast.error(msg);
      // Only block the screen on the *first* attempt (no cached pin); for
      // refreshes we keep the verify screen so the customer can retype.
      if (!cachedPin) setError(msg);
    } finally { setVerifying(false); setLoading(false); }
  };

  if (brandLoading) return <LoadingScreen brand={brand} />;
  if (error === 'Link non valido') return <InvalidLink brand={brand} />;
  if (!verified) return (
    <>
      <VerifyScreen token={token} pin={pin} setPin={setPin} verifying={verifying} onVerify={() => handleVerifyWithPin(pin)} brand={brand} businessName={businessName} />
      <CookieConsentBanner brandId={brandId} />
    </>
  );
  if (loading || !data) return <LoadingScreen brand={brand} />;
  return (
    <>
      <Dashboard data={data} setData={setData} initialTab={tabFromUrl} token={token} pin={pin} />
      <CookieConsentBanner brandId={brandId} />
    </>
  );
}
